/**
 * FPP Settlement (Frequency Performance Payments) — sheet item.
 *
 * Replaced the legacy Causer Pays mechanism on 2025-06-08. For one
 * picked asset over the selected period:
 *   • Headline KPIs — Net $, Performance payment, Residual cost recovery
 *   • Per-bidtype breakdown — RAISEREG vs LOWERREG
 *   • Daily stacked bar chart — perf + residual per bidtype per day
 *
 * Backed by GET /api/portfolios/<id>/fpp-settlement.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Chip,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Alert,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from 'axios';
import BoltIcon from '@mui/icons-material/Bolt';
import { useThemeColors } from '../theme/ThemeModeContext';
import CommercialWorkspaceTabs from './CommercialWorkspaceTabs';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// Sign-aware currency formatter. Negative values render as -$X,XXX.XX
// (a parenthesised accounting style was considered but reads worse in the
// dense card grid).
const fmtAud = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString('en-AU',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '−' : ''}$${abs}`;
};
const fmtAudShort = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}k`;
  return `${sign}$${a.toFixed(0)}`;
};
const fmtCount = (v) => v == null ? '—' : Number(v).toLocaleString('en-AU');

const PERIOD_OPTIONS = [
  { code: '7d',  label: '7D'  },
  { code: '30d', label: '30D' },
  { code: '90d', label: '90D' },
  { code: 'ytd', label: 'YTD' },
];

// Card colour rules: positive net = green (credit); negative = red (charge).
// Headline cards stay neutral on the box, accent the value only.
function deltaColor(v) {
  if (v == null || v === 0) return 'text.primary';
  return v > 0 ? 'success.main' : 'error.main';
}

function HeadlineCard({ label, value, sub, accent }) {
  return (
    <Card variant="outlined" sx={{ flex: '1 1 220px', minWidth: 220 }}>
      <CardContent sx={{ pb: '14px !important', pt: 1.5 }}>
        <Typography sx={{
          fontSize: '0.66rem', color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: '0.06em',
          fontWeight: 700, mb: 0.5,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '1.35rem', fontWeight: 800,
          fontFamily: "'JetBrains Mono', monospace",
          color: accent || 'text.primary',
          lineHeight: 1.1, mb: 0.25,
        }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function BidtypeRow({ label, data }) {
  if (!data) return null;
  return (
    <Box sx={{
      display: 'grid', gap: 1.5, px: 2, py: 1.25,
      gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
      alignItems: 'center',
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary',
                         fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
        {fmtCount(data.intervals)}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600,
                         fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                         color: deltaColor(data.performance_aud) }}>
        {fmtAud(data.performance_aud)}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600,
                         fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                         color: deltaColor(data.residual_aud) }}>
        {fmtAud(data.residual_aud)}
      </Typography>
      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700,
                         fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                         color: deltaColor(data.net_aud) }}>
        {fmtAud(data.net_aud)}
      </Typography>
    </Box>
  );
}

const FppSettlement = () => {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  const [portfolio, setPortfolio] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}`)
      .then((r) => {
        setPortfolio(r.data);
        const first = (r.data.assets || [])[0];
        if (first) setAssetId(first.asset_id);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  useEffect(() => {
    if (!portfolioId || !assetId) return;
    setData(null); setError(null);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/fpp-settlement`,
              { params: { asset_id: assetId, period } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, [portfolioId, assetId, period]);

  if (loading && !portfolio) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={140} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={440} />
      </Box>
    );
  }
  if (!portfolio) return null;
  const assets = portfolio.assets || [];
  if (assets.length === 0) {
    return (
      <Box sx={{ p: 2.5 }}>
        <CommercialWorkspaceTabs />
        <Alert severity="info">This portfolio has no assets.</Alert>
      </Box>
    );
  }

  const t = data?.totals;
  // Build chart series from data.series: 4 stacked bars per day
  // (raise_perf, raise_resid, lower_perf, lower_resid). Performance
  // bars can go positive or negative; residual is always ≤ 0.
  const xDates = (data?.series || []).map((d) => new Date(d.date));

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <CommercialWorkspaceTabs />
      {/* Header strip */}
      <Stack direction="row" alignItems="center" spacing={2}
             sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        <BoltIcon sx={{ fontSize: 22, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          FPP Settlement
        </Typography>
        <Chip size="small" label="Frequency Performance Payments"
              sx={{ fontSize: '0.7rem' }} />
        <Box sx={{ flexGrow: 1 }} />
        {assets.length > 1 && (
          <Select size="small" value={assetId || ''}
                  onChange={(e) => setAssetId(e.target.value)}
                  sx={{ minWidth: 220 }}>
            {assets.map((a) => (
              <MenuItem key={a.asset_id} value={a.asset_id}>
                {a.name} ({a.asset_id})
              </MenuItem>
            ))}
          </Select>
        )}
        <ToggleButtonGroup size="small" value={period} exclusive
                           onChange={(_, v) => v && setPeriod(v)}>
          {PERIOD_OPTIONS.map((p) => (
            <ToggleButton key={p.code} value={p.code} sx={{ px: 1.25 }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Headline cards */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <HeadlineCard
          label="Net settlement"
          value={data ? fmtAud(t?.net_aud) : <Skeleton width={120} />}
          sub={`${data?.period?.label || ''} · ${fmtCount(t?.intervals)} intervals`}
          accent={deltaColor(t?.net_aud)} />
        <HeadlineCard
          label="Performance payment"
          value={data ? fmtAud(t?.performance_aud) : <Skeleton width={120} />}
          sub="CF × MW × payment rate (signed)"
          accent={deltaColor(t?.performance_aud)} />
        <HeadlineCard
          label="Residual cost recovery"
          value={data ? fmtAud(t?.residual_aud) : <Skeleton width={120} />}
          sub="FPP + used/unused FCAS pro-rata MW"
          accent={deltaColor(t?.residual_aud)} />
      </Stack>

      {/* Per-bidtype breakdown */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid',
                    borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            By bid type
          </Typography>
        </Box>
        <Box sx={{
          display: 'grid', gap: 1.5, px: 2, py: 1,
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
          fontSize: '0.66rem', color: 'text.disabled',
          textTransform: 'uppercase', letterSpacing: '0.06em',
          fontWeight: 700,
          borderBottom: '1px solid', borderColor: 'divider',
        }}>
          <Box>Service</Box>
          <Box sx={{ textAlign: 'right' }}>Intervals</Box>
          <Box sx={{ textAlign: 'right' }}>Performance</Box>
          <Box sx={{ textAlign: 'right' }}>Residual</Box>
          <Box sx={{ textAlign: 'right' }}>Net</Box>
        </Box>
        <BidtypeRow label="RAISEREG" data={t?.by_bidtype?.RAISEREG} />
        <BidtypeRow label="LOWERREG" data={t?.by_bidtype?.LOWERREG} />
      </Card>

      {/* Daily stacked bar chart */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Daily breakdown
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {data?.period?.label || ''} · {data?.asset?.name}
            </Typography>
          </Stack>
          {!data ? (
            <Skeleton variant="rectangular" height={440}
                      sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
          ) : xDates.length === 0 ? (
            <Box sx={{ height: 440, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.disabled">
                No FPP settlement data for {data.asset?.name} in this period.
                AEMO FPP coverage starts 2025-06-08; pre-rule data lives in
                the legacy NETWORK_CAUSER_PAYS_SCADA table.
              </Typography>
            </Box>
          ) : (
            <BarChart
              height={440}
              xAxis={[{
                data: xDates,
                scaleType: 'band',
                valueFormatter: (v) => {
                  const d = v instanceof Date ? v : new Date(v);
                  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
                },
                tickLabelStyle: { fontSize: 11 },
              }]}
              yAxis={[{ valueFormatter: (v) => fmtAudShort(v) }]}
              series={[
                { id: 'rp', label: 'Raise · Performance', stack: 'raise',
                  data: data.series.map((d) => d.raise_perf_aud),
                  color: '#86efac',
                  valueFormatter: (v) => v == null ? '—' : fmtAud(v) },
                { id: 'rr', label: 'Raise · Residual', stack: 'raise',
                  data: data.series.map((d) => d.raise_resid_aud),
                  color: '#fca5a5',
                  valueFormatter: (v) => v == null ? '—' : fmtAud(v) },
                { id: 'lp', label: 'Lower · Performance', stack: 'lower',
                  data: data.series.map((d) => d.lower_perf_aud),
                  color: '#22d3ee',
                  valueFormatter: (v) => v == null ? '—' : fmtAud(v) },
                { id: 'lr', label: 'Lower · Residual', stack: 'lower',
                  data: data.series.map((d) => d.lower_resid_aud),
                  color: '#fb923c',
                  valueFormatter: (v) => v == null ? '—' : fmtAud(v) },
              ]}
              margin={{ left: 70, right: 24, top: 16, bottom: 50 }}
              slotProps={{
                legend: { direction: 'horizontal',
                          position: { vertical: 'bottom', horizontal: 'middle' } },
              }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FppSettlement;
